<script setup lang="ts">
useSchemaOrg([
  defineBreadcrumb({
    itemListElement: [
      { name: 'Home', item: '/' },
      { name: 'Integrations', item: '/integrations' },
      { name: 'Supported Affiliate Programs', item: '/integrations/supported' }
    ]
  })
])

useHead({
  title: 'Supported affiliate programs',
  meta: [{ name: 'description', content: 'Supported retailers and affiliate programs at Geniuslink' }]
})
</script>

<template>
  <div class="page">
    <div class="container">
      <div class="row breadcrumb-area">
        <div class="col-lg-8 col-md-10">
          <ol class="breadcrumb">
            <li><NuxtLink to="/">Home</NuxtLink></li>
            <li><NuxtLink to="/integrations">Integrations</NuxtLink></li>
            <li class="active">Supported affiliate programs</li>
          </ol>
        </div>
      </div>

      <section class="row">
        <div class="col-lg-10 col-lg-offset-1 col-md-10 col-md-offset-1">
          <h1>Supported affiliate programs</h1>
          <p>Built-in optimizations that help you earn more money and save time.</p>

          <div class="comparison-area">
            <table class="comparison supported">
              <thead>
                <tr>
                  <th></th>
                  <th class="helptip" data-tipso-title="Auto Affiliation"
                    data-tipso-content="Once you connect an affiliate program, Genius Link instantly affiliates every matching link in your account, whether in a simple link, Choice Page, or other integration.">
                    <span class="help-icon-w"></span> Auto affiliation
                    <p>Save time and simplify your affiliate life.</p>
                  </th>
                  <th class="helptip" data-tipso-title="Auto Localization"
                    data-tipso-content="Genius Link detects a shopper's location and routes them to the same product in their local storefront, where they are most likely to buy.">
                    <span class="help-icon-w"></span> Auto localization
                    <p>Boost earnings effortlessly.</p>
                  </th>
                  <th class="helptip" data-tipso-title="Partner-sponsored"
                    data-tipso-content="These retailers have partnered with us so your clicks to their stores are 100% free.">
                    <span class="help-icon-w"></span> Free clicks
                    <p>Partner-sponsored</p>
                  </th>
                  <th class="helptip" data-tipso-title="Quick-add"
                    data-tipso-content="We scan these retailers' product catalogs, allowing you to effortlessly add links to your Choice Pages and search for links by product name.">
                    <span class="help-icon-w"></span> Quick-add
                    <p>Built-in product search and suggestion</p>
                  </th>
                </tr>
              </thead>
              <thead class="table-section-head">
                <tr>
                  <td colspan="5"> Blanket affiliation support:</td>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Thousands of retailers</td>
                  <td><span class="vsprite vsprite-check-alt"></span>
                    <span class="helptip" data-tipso-title='Affiliate "everything"' data-tipso-content="Connect a free Skimlinks or Sovrn Commerce account to your Geniuslink account, and we'll automatically affiliate every supported link. Note, Sovrn Commerce or Skimlinks commission share rates apply.">
                      Via <a target="_blank" href="https://geni.us/VigLinkSignup" rel="noopener noreferrer">Sovrn Commerce</a> or
                      <a target="_blank" href="https://geni.us/SkimlinksSignup" rel="noopener noreferrer">Skimlinks</a></span>
                  </td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              </tbody>
              <thead class="table-section-head">
                <tr>
                  <td colspan="5"> Built-in support:</td>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <a target="_blank" href="https://geniuslink.com/blog/adorama-affiliate-program/" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=5b75aca686a544000817740b" />
                      Adorama</a>
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=57bb3137b61170347dba229e" />
                    Amazon <span class="fineprint">(all regions)</span>
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=65132f1cd1535d000968041c" />
                    Anker
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <a target="_blank" href="https://www.apple.com/itunes/affiliates/" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=5cf06117217a21000756c917" />
                      Apple Books</a>
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <a target="_blank" href="https://www.apple.com/itunes/affiliates/" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=5cf04119217a21000756c913" />
                      Apple Music / iTunes</a>
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <a target="_blank" href="https://geniuslink.com/blog/backcountry-affiliate-program/" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=5e852c65155fc70008a1eaed" />
                      Backcountry</a>
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <a target="_blank" href="https://geniuslink.com/blog/bh-photo-affiliate-program/" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=5a5fd6292e44f51f358f6ca5" />
                      B&amp;H Photo Int.</a>
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <a target="_blank" href="https://www.basspro.com/shop/en/affiliate-program" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=5e84daf5ac05040008a3e3f2" />
                      Bass Pro Shop</a>
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <a target="_blank" href="https://geniuslink.com/blog/barnes-and-noble-affiliate-program/" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=57bdf5f4b6117022cb86cd77" />
                      Barnes &amp; Noble</a>
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
                <tr>
                  <td>
                    <a target="_blank" href="https://geniuslink.com/blog/best-buy-affiliate-program/" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=57be03d8b6117022cb86cd78" />
                      Best Buy (US)</a>
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <a target="_blank" href="https://geniuslink.com/blog/bloomingdales-affiliate-program/" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=5e84dd5aac05040008a3e3f3" />
                      Bloomingdale's</a>
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <a target="_blank" href="https://geniuslink.com/blog/books-a-million-affiliate-program/" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=5b04a3a64a99400008bea3f7" />
                      Books-A-Million</a>
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=5e5571a35d3a3700086d0527" />
                    Bookshop
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <a target="_blank" href="https://geniuslink.com/blog/chewy-affiliate-program/" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=5c14449d2860cd0008644efd" />
                      Chewy</a>
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=65132ef02ee33100082bec72" />
                    Coco &amp; Eve
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=5c1432eff2d54a0007222ad8" />
                    Crutchfield
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=65132f450a4aca0008edcdb0" />
                    Dermaflash
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <a target="_blank" href="https://geniuslink.com/blog/dicks-sports-affiliate-program/" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=5e852b23155fc70008a1eaec" />
                      Dick's</a>
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=65132d850a4aca0008edcdaf" />
                    Dr. Brandt
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <a target="_blank" href="https://geniuslink.com/blog/ebay-affiliate-program/" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=57bb315cb7bbf3356581bcbb" />
                      eBay</a>
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
                <tr>
                  <td>
                    <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=65132d22d1535d000968041b" />
                    Eufy
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <a target="_blank" href="https://geniuslink.com/blog/gamestop-affiliate-program/" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=5f7e10ab6bf9fc00085cb77f" />
                      GameStop</a>
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <a target="_blank" href="https://www.homedepot.com/c/SF_MS_The_Home_Depot_Affiliate_Program" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=5e84fed4bee2b80008f5eaac" />
                      The Home Depot</a>
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <a target="_blank" href="https://geniuslink.com/blog/hp-affiliate-program/" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=60f9bb1702b7630008113f6b" />
                      HP</a>
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <a target="_blank" href="https://geniuslink.com/blog/iherb-affiliate-program/" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=5eea7b8200362900098b1ed0" />
                      iHerb</a>
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <a target="_blank" href="https://www.apple.com/itunes/affiliates/" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=57bb310c5e1972522f03aa77" />
                      iTunes</a>
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <a target="_blank" href="https://geniuslink.com/blog/kohls-affiliate-program/" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=5e84e2ff1ec94d00084a58a6" />
                      Kohl's</a>
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <a target="_blank" href="https://www.kobo.com/us/en/p/affiliate" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=57e5c168b7bbf35e26fd4eb0" />
                      Kobo</a>
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <a target="_blank" href="https://geniuslink.com/blog/lowes-affiliate-program/" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=5e8501e4bee2b80008f5eab4" />
                      Lowe's</a>
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <a target="_blank" href="https://www.bloomingdales.com/b/about-us/affiliate-program-network-partner" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=5e85013dbee2b80008f5eab3" />
                      Macy's</a>
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=65132ca32ee33100082bec70" />
                    Max
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <a href="https://geniuslink.com/blog/microsoft-affiliate-program/" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=5ca68c5bd115e90008ff0fdc" />
                      Microsoft Store</a>
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <a target="_blank" href="https://geniuslink.com/blog/moment-affiliate-program/" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=5e8552be872dc7000a2203f0" />
                      Moment</a>
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=65132f702ee33100082bec73" />
                    Motorola
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <a target="_blank" href="https://geniuslink.com/blog/newegg-affiliate-program/" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=57bb3180b7bbf3356581bcbd" />
                      Newegg</a>
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=5e84ff93bee2b80008f5eaaf" />
                    Overstock
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=6513304f8c506c0008c58cec" />
                    Samsung
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=65132ebc2ee33100082bec71" />
                    Snooz
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=65132cf08c506c0008c58cea" />
                    Soundcore
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=65132d508c506c0008c58ceb" />
                    StockX
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <a target="_blank" href="https://geniuslink.com/blog/target-affiliate-program/" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=57be0487b6117022cb86cd80" />
                      Target</a>
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=5e84fe76bee2b80008f5eaaa" />
                    Tiger Direct
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <a target="_blank" href="https://geniuslink.com/blog/walmart-affiliate-program/" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=57be04ae2e44f572f5c93114" />
                      Walmart (US)</a>
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=5e84fd031ec94d00084a58ab" />
                    Wordery
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
                <tr>
                  <td>
                    <a target="_blank" href="https://geniuslink.com/blog/wayfair-affiliate-program/" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=5c144239f2d54a0007222adc" />
                      Wayfair</a>
                  </td>
                  <td><span class="vsprite vsprite-check"></span></td>
                  <td></td>
                  <td></td>
                  <td><span class="vsprite vsprite-check"></span></td>
                </tr>
              </tbody>
              <thead class="table-section-head">
                <tr>
                  <td colspan="5">Affiliate aggregators:</td>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <a target="_blank" href="https://geni.us/SkimlinksSignup" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=5d15375a1c41a90009332369" />
                      Skimlinks</a>
                  </td>
                  <td><span class="vsprite vsprite-check-alt"></span></td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
                <tr>
                  <td>
                    <a target="_blank" href="https://geni.us/VigLinkSignup" rel="noopener noreferrer">
                      <img class="supported-logo" src="https://smallimage.cdn.geni.us/?ImageId=5e18c1be1423380008ff2f52" />
                      Sovrn Commerce</a>
                  </td>
                  <td><span class="vsprite vsprite-check-alt"></span></td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>

          <p class="fineprint"><span class="vsprite vsprite-check-alt"></span> All major retailers covered by connecting Sovrn Commerce or Skimlinks to your Geniuslink account. Commission share rates apply.</p>
          <p class="fineprint"><span class="vsprite vsprite-check"></span> Connect this retailer program to your Geniuslink account. You get 100% of commissions.</p>
        </div>
      </section>
    </div>
  </div>
</template>

